import { useState, useRef, useEffect, useCallback, lazy, Suspense } from 'react'
import { NavLink } from 'react-router-dom'
import { clsx } from 'clsx'
import { useAuthStore } from '../../store/authStore'
import { useTerminalStore } from '../../store/terminalStore'
import { useAlertStore } from '../../store/alertStore'
import { marketApi, type SearchResult } from '../../api/market'
import { useQuery } from '@tanstack/react-query'
import { useMobile } from '../../hooks/useMobile'

// Lazy: ExportModal pulls in xlsx + jspdf (~700kB min) — keep out of the main bundle
const ExportModal = lazy(() =>
  import('../ui/ExportModal').then((m) => ({ default: m.ExportModal })),
)

const NAV = [
  { to: '/terminal',    label: 'TERMINAL',    tip: 'Live quotes, candlestick charts, RSI/MACD/BB indicators, AI analysis, and paper trading' },
  { to: '/multi-chart', label: 'MULTI-CHART', tip: 'Up to 6 independent chart panes — each with its own symbol, period, and chart type' },
  { to: '/research',    label: 'RESEARCH',    tip: 'Company research (web snapshot, fundamentals, VC diligence) · AI Committee decision · SEC 10-K reader' },
  { to: '/risk',        label: 'RISK',        tip: 'Physical asset map, geopolitical events, supply chain, and FMEA risk scoring' },
  { to: '/portfolio',   label: 'PORTFOLIO',   tip: 'Holdings, optimizer, backtesting, and portfolio simulator in one hub' },
  { to: '/bot',         label: 'STRATEGY IDEAS (PAPER ONLY)', tip: 'AI-generated paper trading ideas with mandatory human approval gate — no real money' },
  { to: '/strategy-lab',label: 'AI STRATEGY LAB',  tip: 'Backtest any strategy, then let Claude analyze weaknesses and suggest improvements' },
  { to: '/options',     label: 'OPTIONS',     tip: 'Full options chain with implied volatility, open interest, and Black-Scholes Greeks' },
  { to: '/screener',    label: 'SCREENER',    tip: 'Stock screener — filter 800+ symbols by RSI, P/E, market cap, quant scores, and sector' },
  { to: '/crypto',      label: 'CRYPTO',      tip: 'Cryptocurrency market overview — prices, market cap, and 24h changes for top 50 coins' },
  { to: '/flows',       label: 'FLOWS',       tip: 'Institutional 13F data, dark pool short volume, and congressional trade disclosures' },
  { to: '/macro',       label: 'MACRO',       tip: 'FRED macro charts — Fed Funds Rate, CPI, unemployment, GDP, yield curve, VIX' },
  { to: '/events',      label: 'EVENTS',      tip: 'Earnings calendar, economic releases, and dividend schedule in one hub' },
  { to: '/sectors',     label: 'SECTORS',     tip: 'S&P 500 sector heatmap and sector rotation relative-strength signals' },
  { to: '/social',      label: 'SOCIAL',      tip: 'StockTwits + Reddit WSB sentiment stream — bullish/bearish message flow for any ticker' },
  { to: '/watchlist',   label: 'WATCHLIST',   tip: 'Manage your watchlist with live signals, price changes, and Excel export' },
  { to: '/seasonality', label: 'SEASONALITY', tip: 'Historical monthly return analysis — best/worst months, win rates, seasonal patterns for any symbol' },
  { to: '/calculators', label: 'CALCULATORS', tip: 'Risk/reward, position sizer, compounding growth, and CAGR calculators' },
  { to: '/messages',    label: 'MESSAGES',    tip: 'Team messaging and channel-based collaboration for trading notes and alerts' },
  { to: '/guide',       label: 'GUIDE',       tip: 'User guide — detailed documentation for every tab, feature, and function with appendix' },
  { to: '/settings',    label: 'SETTINGS',    tip: 'AI provider keys, Alpaca brokerage connection, dark/light mode, and keyboard shortcuts' },
  { to: '/admin',       label: 'ADMIN',       tip: 'Site manager dashboard — user metrics, audit chain, trust scores, AI artifacts' },
]

const TYPE_COLOR: Record<string, string> = {
  Equity:         'text-bb-green',
  ETF:            'text-blue-400',
  'Mutual Fund':  'text-purple-400',
  Cryptocurrency: 'text-bb-orange',
  Index:          'text-bb-subtle',
}

function TickerLogo({ symbol }: { symbol: string }) {
  const [failed, setFailed] = useState(false)
  const src = `https://assets.parqet.com/logos/symbol/${symbol}?format=png`
  if (failed) {
    return (
      <span aria-hidden="true" className="w-5 h-5 rounded-full bg-bb-border flex items-center justify-center text-2xs font-bold text-bb-orange shrink-0">
        {symbol[0]}
      </span>
    )
  }
  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      loading="lazy"
      className="w-5 h-5 rounded-full object-contain bg-white/5 shrink-0"
      onError={() => setFailed(true)}
    />
  )
}

export function TopBar() {
  const { setSymbol, activeSymbol, liveData, setLiveData, setHotkeyModalOpen, chartPeriod, chartInterval } = useTerminalStore()
  const [exportOpen, setExportOpen] = useState(false)
  const isMobile = useMobile()
  const { signOut, user } = useAuthStore()
  const { alerts, resetTriggered } = useAlertStore()
  const [input, setInput]         = useState('')
  const [results, setResults]     = useState<SearchResult[]>([])
  const [open, setOpen]           = useState(false)
  const [highlighted, setHighlighted] = useState(-1)
  const [searching, setSearching]     = useState(false)
  const [notifOpen, setNotifOpen]     = useState(false)
  const searchRef    = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const notifRef     = useRef<HTMLDivElement>(null)
  const debounceRef  = useRef<ReturnType<typeof setTimeout> | null>(null)

  const triggeredAlerts = alerts.filter((a) => a.triggered)

  // '/' shortcut → focus search
  useEffect(() => {
    const focus = () => { searchRef.current?.focus(); searchRef.current?.select() }
    window.addEventListener('bb:focus-search', focus)
    return () => window.removeEventListener('bb:focus-search', focus)
  }, [])

  // Close dropdowns when clicking/touching outside
  useEffect(() => {
    const handler = (e: MouseEvent | TouchEvent) => {
      const target = (e as TouchEvent).touches?.[0]?.target ?? (e as MouseEvent).target
      if (containerRef.current && !containerRef.current.contains(target as Node)) {
        setOpen(false)
      }
      if (notifRef.current && !notifRef.current.contains(target as Node)) {
        setNotifOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    document.addEventListener('touchstart', handler, { passive: true })
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('touchstart', handler)
    }
  }, [])

  const { data: quote } = useQuery({
    queryKey: ['quote', activeSymbol],
    queryFn:  () => marketApi.quote(activeSymbol),
    refetchInterval: liveData ? 5_000 : false,
  })

  const runSearch = useCallback((q: string) => {
    if (q.length < 1) { setResults([]); setOpen(false); setSearching(false); return }
    setSearching(true)
    setOpen(true)
    marketApi.search(q)
      .then((res) => { setResults(res); setOpen(true); setHighlighted(-1) })
      .catch(() => { setResults([]) })
      .finally(() => setSearching(false))
  }, [])

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setInput(val)
    if (!val) { setResults([]); setOpen(false); setSearching(false); return }
    setOpen(true)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => runSearch(val), 220)
  }

  const selectResult = (r: SearchResult) => {
    setSymbol(r.symbol)
    marketApi.prefetch(r.symbol)  // warm cache before components mount
    setInput('')
    setResults([])
    setOpen(false)
    setHighlighted(-1)
    searchRef.current?.blur()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlighted((h) => Math.min(h + 1, results.length - 1))
      if (!open && results.length) setOpen(true)
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlighted((h) => Math.max(h - 1, 0))
      return
    }
    if (e.key === 'Escape') { setOpen(false); setResults([]); setInput(''); return }
    if (e.key === 'Enter') {
      e.preventDefault()
      if (highlighted >= 0 && results[highlighted]) {
        selectResult(results[highlighted])
      } else if (results.length > 0) {
        selectResult(results[0])
      }
      // No fallback — symbol is only ever set by selecting a validated search result
    }
  }

  return (
    <>
    <header className="border-b border-bb-border bg-bb-surface shrink-0">

      {/* Top row: logo + quote + search + controls */}
      <div className="flex items-center gap-2 px-3 py-1 min-w-0">
        {/* Logo */}
        <span className="text-bb-orange font-bold text-base tracking-widest shrink-0">QuantTradeAI</span>

        {/* Active quote */}
        {quote && (
          <div className="hidden sm:flex items-center gap-1.5 text-xs shrink-0">
            <span className="text-bb-text font-semibold">{quote.symbol}</span>
            <span className="text-bb-text">{quote.price.toFixed(2)}</span>
            <span className={quote.change >= 0 ? 'text-bb-green' : 'text-bb-red'}>
              {quote.change >= 0 ? '+' : ''}{quote.change.toFixed(2)} ({quote.changePercent.toFixed(2)}%)
            </span>
          </div>
        )}

        {/* Search */}
        <div className="relative flex-1 min-w-0" ref={containerRef} role="search">
          <label htmlFor="bb-global-search" className="bb-sr-only">Search ticker or company</label>
          <input
            ref={searchRef}
            id="bb-global-search"
            className="bb-input w-full"
            value={input}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            onFocus={() => { if (results.length) setOpen(true) }}
            placeholder="Search ticker or company… [/]"
            autoComplete="off"
            spellCheck={false}
            role="combobox"
            aria-expanded={open}
            aria-controls="bb-search-listbox"
            aria-autocomplete="list"
            aria-activedescendant={highlighted >= 0 && results[highlighted] ? `bb-search-opt-${results[highlighted].symbol}` : undefined}
          />
          {input.includes('.') && !open && (
            <div className="absolute left-0 top-full mt-0.5 z-50 bg-bb-surface border border-bb-border text-xs text-bb-muted p-2 rounded shadow-lg whitespace-nowrap">
              International suffixes: .NS (India) · .L (London) · .HK (Hong Kong) · .AX (Australia) · .TO (Toronto) · .DE (Frankfurt) · .T (Tokyo)
            </div>
          )}
          {(input.includes('=') || input.toUpperCase().endsWith('=F') || input.toUpperCase().endsWith('=X')) && !open && (
            <div className="absolute left-0 top-full mt-0.5 z-50 bg-bb-surface border border-bb-border text-2xs text-bb-muted p-2 rounded shadow-lg space-y-1 min-w-max">
              <div className="text-bb-subtle font-medium">Futures (=F) &amp; Forex (=X)</div>
              <div className="text-bb-disabled">Futures: ES=F (S&amp;P500) · NQ=F (Nasdaq) · CL=F (Crude Oil) · GC=F (Gold) · SI=F (Silver) · ZB=F (T-Bond)</div>
              <div className="text-bb-disabled">Forex: EURUSD=X · GBPUSD=X · USDJPY=X · AUDUSD=X · USDCAD=X · USDCHF=X</div>
            </div>
          )}
          {open && (searching || results.length > 0) && (
            <div
              id="bb-search-listbox"
              role="listbox"
              aria-label="Search results"
              className="absolute left-0 top-full mt-0.5 w-full sm:w-96 z-50 bg-bb-surface border border-bb-border rounded shadow-xl overflow-hidden"
            >
              {searching && results.length === 0 ? (
                <div className="flex items-center gap-2 px-3 py-2.5 text-xs text-bb-disabled" role="status" aria-live="polite">
                  <span className="w-3 h-3 border border-bb-disabled border-t-bb-subtle rounded-full animate-spin shrink-0" aria-hidden="true" />
                  Searching global markets…
                </div>
              ) : (
                results.map((r, i) => (
                  <button
                    key={r.symbol}
                    id={`bb-search-opt-${r.symbol}`}
                    role="option"
                    aria-selected={i === highlighted}
                    className={clsx(
                      'w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors',
                      i === highlighted ? 'bg-bb-border' : 'hover:bg-bb-border/50'
                    )}
                    onMouseDown={(e) => { e.preventDefault(); selectResult(r) }}
                    onMouseEnter={() => setHighlighted(i)}
                  >
                    <TickerLogo symbol={r.symbol} />
                    <span className="font-mono font-bold w-20 shrink-0 text-bb-orange truncate">{r.symbol}</span>
                    <span className="flex-1 truncate text-bb-subtle">{r.name}</span>
                    <span className="shrink-0 text-2xs text-bb-disabled">{r.exchange}</span>
                    <span className={clsx('shrink-0 text-2xs w-14 text-right', TYPE_COLOR[r.type] ?? 'text-bb-disabled')}>
                      {r.type}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Live data toggle */}
        <button
          onClick={() => setLiveData(!liveData)}
          aria-pressed={liveData}
          aria-label={liveData ? 'Live data on, click to pause' : 'Live data paused, click to resume'}
          className={clsx(
            'flex items-center gap-1 text-2xs px-2 py-0.5 rounded-sm border transition-colors shrink-0',
            liveData
              ? 'border-bb-green/50 text-bb-green bg-bb-green/10'
              : 'border-bb-border text-bb-disabled hover:text-bb-subtle'
          )}
          title={liveData ? 'Live data ON — click to pause' : 'Live data OFF — click to resume'}
        >
          <span aria-hidden="true" className={clsx('w-1.5 h-1.5 rounded-full', liveData ? 'bg-bb-green animate-pulse' : 'bg-bb-disabled')} />
          <span className="hidden sm:inline">{liveData ? 'LIVE' : 'PAUSED'}</span>
        </button>

        {/* Export button */}
        <button
          onClick={() => setExportOpen(true)}
          aria-label="Export report as Excel or PDF"
          className="flex items-center gap-1 text-2xs px-2 py-0.5 rounded-sm border border-bb-border text-bb-disabled hover:text-bb-subtle hover:border-bb-border transition-colors shrink-0"
          title="Export report (Excel / PDF)"
        >
          <svg aria-hidden="true" className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
          </svg>
          <span className="hidden sm:inline">EXPORT</span>
        </button>

        {/* Notification bell */}
        <div className="relative shrink-0" ref={notifRef}>
          <button
            onClick={() => setNotifOpen((p) => !p)}
            aria-label={triggeredAlerts.length > 0 ? `Notifications, ${triggeredAlerts.length} triggered alert${triggeredAlerts.length === 1 ? '' : 's'}` : 'Notifications, none triggered'}
            aria-haspopup="dialog"
            aria-expanded={notifOpen}
            className="relative p-1.5 text-bb-subtle hover:text-bb-text transition-colors"
            title="Notifications"
          >
            <svg aria-hidden="true" className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {triggeredAlerts.length > 0 && (
              <span aria-hidden="true" className="absolute top-0.5 right-0.5 w-2 h-2 bg-bb-orange rounded-full" />
            )}
          </button>
          {notifOpen && (
            <div className="absolute right-0 top-full mt-1 w-screen max-w-xs sm:w-72 z-50 bg-bb-surface border border-bb-border rounded shadow-xl overflow-hidden">
              <div className="px-3 py-2 border-b border-bb-border flex items-center justify-between">
                <span className="text-xs font-semibold text-bb-text">Triggered Alerts</span>
                {triggeredAlerts.length > 0 && (
                  <button
                    onClick={() => triggeredAlerts.forEach((a) => resetTriggered(a.id))}
                    className="text-2xs text-bb-disabled hover:text-bb-subtle"
                  >Clear all</button>
                )}
              </div>
              {triggeredAlerts.length === 0 ? (
                <p className="text-2xs text-bb-disabled text-center py-4">No triggered alerts</p>
              ) : (
                triggeredAlerts.map((a) => (
                  <div key={a.id} className="flex items-center justify-between px-3 py-2 border-b border-bb-border/30 last:border-0">
                    <div>
                      <span className="text-xs font-semibold text-bb-orange mr-1.5">{a.symbol}</span>
                      <span className="text-2xs text-bb-subtle">
                        {a.metric.toUpperCase()} {a.condition} {a.threshold}
                      </span>
                    </div>
                    <button
                      onClick={() => resetTriggered(a.id)}
                      className="text-bb-disabled hover:text-bb-red text-xs ml-2"
                    >✕</button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* User info + logout */}
        <div className="flex items-center gap-2 shrink-0 ml-auto">
          <span className="hidden md:inline text-2xs text-bb-disabled">{user?.email?.split('@')[0]}</span>
          <button onClick={signOut} className="bb-btn-ghost text-2xs">LOGOUT</button>
        </div>
      </div>

      {/* Nav row — desktop only; mobile uses MobileBottomNav */}
      {!isMobile && (
        <nav className="bb-nav-scroll border-t border-bb-border/50" aria-label="Primary navigation">
          <div className="flex items-center px-2 py-0.5 gap-0.5 w-max min-w-full">
            {NAV.map(({ to, label, tip }) => (
              <NavLink
                key={to}
                to={to}
                title={tip}
                aria-label={`${label}: ${tip}`}
                className={({ isActive }) =>
                  clsx(
                    'px-2 py-1 text-xs font-medium transition-colors whitespace-nowrap rounded-sm',
                    isActive
                      ? 'text-bb-orange bg-bb-orange/15 border-b border-bb-orange'
                      : 'text-bb-subtle hover:text-bb-text hover:bg-bb-border/40'
                  )
                }
              >
                {label}
              </NavLink>
            ))}
            {/* Help / shortcuts button — always pinned at end */}
            <button
              onClick={() => setHotkeyModalOpen(true)}
              title="Keyboard shortcuts & tips [?]"
              aria-label="Open keyboard shortcuts and tips"
              aria-haspopup="dialog"
              className="ml-1 px-2 py-1 text-xs font-medium transition-colors whitespace-nowrap rounded-sm text-bb-disabled hover:text-bb-orange hover:bg-bb-orange/15 border border-bb-border/60 hover:border-bb-orange/40"
            >
              ?  HELP
            </button>
          </div>
        </nav>
      )}

    </header>

    {exportOpen && (
      <Suspense fallback={null}>
        <ExportModal
          open={exportOpen}
          onClose={() => setExportOpen(false)}
          symbol={activeSymbol}
          company={quote?.name}
          period={chartPeriod}
          interval={chartInterval}
        />
      </Suspense>
    )}
    </>
  )
}
